/// <reference types="@types/google.maps" />
import { FocusEventHandler, SyntheticEvent, useEffect, useState } from 'react';
import { isMobile } from 'react-device-detect';
import { Path, PathValue } from 'react-hook-form';
import parse from 'autosuggest-highlight/parse';
import { LocationOn as LocationOnIcon } from '@mui/icons-material';
import { Avatar, ListItem, ListItemAvatar, ListItemText } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import { CityLocation, MaybeNull, MaybeString, newId, RecordType } from '@petconsole/pure-base';
import { SetState } from '@petconsole/pure-shared';
import useOurDarkMode from '../../hooks/useOurDarkMode';
import useOurGooglePlaces from '../../hooks/useOurGooglePlaces';
import getInputProps, { GetInputProps } from '../../inputs/react-hook-form/helpers/getInputProps';
import getFieldProps from '../inputs/helpers/getFieldProps';
import OurText, { OurTextProps } from '../inputs/OurText';

type AutocompletePrediction = google.maps.places.AutocompletePrediction;
type MaybeAutocompletePrediction = MaybeNull<AutocompletePrediction>;

export interface OurGooglePlacesProps<T extends RecordType = RecordType> extends OurTextProps<T> {
  googlePlaces: ReturnType<typeof useOurGooglePlaces>;
  inputValue: string;
  setInputValue: SetState<string>;
}

const OurGooglePlaces = <T extends RecordType = RecordType>({
  googlePlaces,
  helperText,
  inputValue,
  setInputValue,
  required = false,
  autoFocus = false,
  ...restProps
}: OurGooglePlacesProps<T>) => {
  const darkMode = useOurDarkMode();
  const { form, allowLastPass, formik } = restProps;
  const { autocompleteService, placesService, getPlacePredictions, getPlaceDetails } = googlePlaces;

  const fieldProps = form
    ? getInputProps({ ...restProps, helperText, darkMode } as GetInputProps)
    : getFieldProps({ ...restProps, helperText, darkMode });

  const { id = '', name, label, variant, error, helperText: helpText, InputProps } = fieldProps;

  const [acValue, setAcValue] = useState<MaybeAutocompletePrediction>(null);
  const [acOptions, setAcOptions] = useState<AutocompletePrediction[]>([]);

  useEffect(() => {
    setInputValue(inputValue);
  }, [inputValue, setInputValue]);

  useEffect(() => {
    let active = true;

    if (!autocompleteService || !placesService) return undefined;

    if (inputValue === '') {
      setAcOptions(acValue ? [acValue] : []);

      return undefined;
    }

    getPlacePredictions({ input: inputValue }, (results: AutocompletePrediction[]) => {
      if (active) setAcOptions([...(acValue ? [acValue] : []), ...(results || [])]);
    });

    return () => {
      active = false;
    };
  }, [acValue, inputValue, autocompleteService, placesService, getPlacePredictions]);

  const handleChange = (value: MaybeNull<CityLocation>) => {
    if (form)
      form.setValue(id as Path<T>, (value === null ? { description: '' } : value) as PathValue<T, Path<T>>, {
        shouldDirty: true,
      });
    else if (formik) formik.handleChange({ target: { id, value } });
  };

  const acLocationChange = (newValue: MaybeAutocompletePrediction) => {
    setAcOptions(newValue ? [newValue] : []);
    setAcValue(newValue);

    return newValue ? getPlaceDetails(newValue) : Promise.resolve(null);
  };

  const renderOption = (props: object, option: AutocompletePrediction) => {
    const { structured_formatting } = option;

    const { main_text, secondary_text, main_text_matched_substrings: matches } = structured_formatting;
    const parts = parse(
      main_text,
      matches.map((match) => [match.offset, match.offset + match.length]),
    );

    const primary = parts.map(({ text, highlight }) => (
      <span key={newId()} style={{ fontWeight: highlight ? 700 : 400 }}>
        {text}
      </span>
    ));

    return (
      <ListItem key={newId()} {...props} /* alignItems="flex-start" */>
        <ListItemAvatar sx={{ minWidth: 48, mt: 0 }}>
          <Avatar sx={{ width: 24, height: 24 }}>
            <LocationOnIcon />
          </Avatar>
        </ListItemAvatar>
        <ListItemText primary={primary} secondary={secondary_text} />
      </ListItem>
    );
  };

  const onLocationInputChange = (event: SyntheticEvent, newValue: string) => {
    // This will ensure the initial value doesn't get wiped out
    // if (event) acSetInputValue(newValue);
    if (!event) return;

    event.preventDefault();

    if (event.type === 'blur') return;

    setInputValue(newValue);

    if (!newValue && (form?.getValues('address' as Path<T>) || formik?.values?.address)) handleChange(null);
  };

  const onLocationChange = async (_event: SyntheticEvent<Element, Event>, newValue: AutocompletePrediction | null) => {
    try {
      const acResults = await acLocationChange(newValue);

      handleChange(acResults as MaybeNull<CityLocation>);
    } catch (e) {
      return console.log('Location change error:', e);
    }
  };

  // If the value is cleared, this will ensure we update the field on exiting it
  const onLocationBlur: FocusEventHandler<{ value: MaybeString }> = (event) => {
    if (!event?.target?.value && form) handleChange(null);
    if (!event?.target?.value && formik) handleChange(null);
  };

  const getOptionLabel = (option: MaybeAutocompletePrediction) => option?.description || '';

  return (
    <Autocomplete
      id={id}
      name={name}
      getOptionLabel={getOptionLabel}
      filterOptions={(x) => x}
      options={acOptions}
      // autoComplete
      fullWidth
      includeInputInList
      filterSelectedOptions
      value={acValue}
      inputValue={inputValue}
      onChange={onLocationChange}
      onInputChange={onLocationInputChange}
      onBlur={onLocationBlur}
      {...restProps}
      renderOption={renderOption}
      renderInput={(params) => (
        <OurText<T>
          {...params}
          {...{ label, variant, error, helperText: helpText, required }}
          fullWidth
          autoFocus={autoFocus && !isMobile}
          inputProps={{
            ...params.inputProps,
            ...InputProps,
            ...(allowLastPass ? {} : { 'data-lpignore': true, autoComplete: 'off' }),
          }}
        />
      )}
    />
  );
};

OurGooglePlaces.whyDidYouRender = true;

export default OurGooglePlaces;
